// @ts-strict
import { sample } from 'shared/src/utils/sample';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Skills from '../uTheory.Skills/skills';
import utils from '../utils';
import CheckMark from './checkMark';
import ListSorter from './tableListSorter';
import { toHTML } from '../uTheory.Font/font';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

const sharp = toHTML('accidental', '#', { ariaSpaceBefore: true });
const flat = toHTML('accidental', 'b', { ariaSpaceBefore: true });

const settings = {
  instructions: true,
  letters: {
    checkButton: true,
    letterButtons: false,
  },
  progressTracker: true,
  checkMark: {},
};

const layout = {
  letters: { percent: 10 },
};

export interface CircleOfFifthsSortOptions extends InteractiveOptions {
  sharpsOrFlats?: 'sharps' | 'flats';
  quality?: 'Major' | 'minor' | 'both';
  includeRelativeMinors?: boolean;
}

export default class CircleOfFifthsSort extends InteractivePrototype {
  declare options: CircleOfFifthsSortOptions;

  private skills: ('circle_of_fifths' | 'relative_keys')[] = ['circle_of_fifths'];

  private attempts = 0;

  private listDiv?: HTMLDivElement;

  private listSorter?: ListSorter;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: CircleOfFifthsSortOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = ['circle_of_fifths'];
    this.settings = utils.clone(settings, true);
    this.layout = utils.clone(layout, true);
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: CircleOfFifthsSortOptions) {
    if (!options.sharpsOrFlats && options.drill) {
      options.sharpsOrFlats = sample(['sharps', 'flats']);
    }
    if (!options.quality && options.drill) {
      if (!options.skills) {
        throw Error('options.skills must be set in drill mode if options.quality is not provided.');
      }
      options.quality =
        options.skills.map(Skills.makeString).indexOf('relative_keys') !== -1 ? 'both' : 'Major';
    }
    if (options.quality === 'both') {
      this.skills.push('relative_keys');
    }

    return super.init(displayDiv, options).then(() => {
      this.progressTracker!.start(this.targetSpeed);
      this.letterPanel!.addCheckCallback(this.checkAnswer, this);
      this.attempts = 0;
    });
  }

  displayInstructions() {
    this.instructionsDiv!.innerHTML = 'Match the number of accidentals, keys, and last accidental.';
  }

  createCustomDivs() {
    this.listDiv = this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv!);
    this.checkMark = new CheckMark(this.listDiv);
  }

  initializeCustomElements() {
    const numberOfSharpsOrFlats = [
      `No. ${sharp}/${flat}`,
      '0',
      `1${sharp}`,
      `2${sharp}`,
      `3${sharp}`,
      `4${sharp}`,
      `5${sharp}`,
      `6${sharp}`,
      `7${sharp}`,
      '0',
      `1${flat}`,
      `2${flat}`,
      `3${flat}`,
      `4${flat}`,
      `5${flat}`,
      `6${flat}`,
      `7${flat}`,
    ];

    const lastAccidentals = [
      'Last accidental:',
      '-',
      `F${sharp}`,
      `C${sharp}`,
      `G${sharp}`,
      `D${sharp}`,
      `A${sharp}`,
      `E${sharp}`,
      `B${sharp}`,
      '-',
      `B${flat}`,
      `E${flat}`,
      `A${flat}`,
      `D${flat}`,
      `G${flat}`,
      `C${flat}`,
      `F${flat}`,
    ];

    const majorKeys = [
      'Major key',
      'C Maj',
      'G Maj',
      'D Maj',
      'A Maj',
      'E Maj',
      'B Maj',
      `F${sharp}&nbsp;Maj`,
      `C${sharp}&nbsp;Maj`,
      'C Maj',
      'F Maj',
      `B${flat}&nbsp;Maj`,
      `E${flat}&nbsp;Maj`,
      `A${flat}&nbsp;Maj`,
      `D${flat}&nbsp;Maj`,
      `G${flat}&nbsp;Maj`,
      `C${flat}&nbsp;Maj`,
    ];

    const minorKeys = [
      'Minor key:',
      'A&nbspmin',
      'E&nbspmin',
      'B&nbspmin',
      `F${sharp}&nbspmin`,
      `C${sharp}&nbspmin`,
      `G${sharp}&nbspmin`,
      `D${sharp}&nbspmin`,
      `A${sharp}&nbspmin`,
      'A&nbspmin',
      'D&nbspmin',
      'G&nbspmin',
      'C&nbspmin',
      'F&nbspmin',
      `B${flat}&nbspmin`,
      `E${flat}&nbspmin`,
      `A${flat}&nbspmin`,
    ];

    switch (this.options.sharpsOrFlats) {
      case 'sharps':
        numberOfSharpsOrFlats.splice(9, 8);
        lastAccidentals.splice(9, 8);
        majorKeys.splice(9, 8);
        minorKeys.splice(9, 8);
        break;
      case 'flats':
        numberOfSharpsOrFlats.splice(2, 8);
        lastAccidentals.splice(2, 8);
        majorKeys.splice(2, 8);
        minorKeys.splice(2, 8);
        break;
    }

    const lists = [numberOfSharpsOrFlats];

    switch (this.options.quality) {
      case 'Major':
        lists.push(majorKeys);
        break;
      case 'minor':
        lists.push(minorKeys);
        break;
      case 'both':
        lists.push(majorKeys);
        lists.push(minorKeys);
        break;
    }

    lists.push(lastAccidentals);

    this.listSorter = new ListSorter(this.listDiv!, lists, { stripes: 'vertical' });
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  gameOver() {
    this.lettersDiv!.style.display = 'none';
    this.listSorter?.gameOver();
    super.gameOver();
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  checkAnswer(debugRightAnswer?: boolean) {
    this.attempts++;
    const answers = this.listSorter!.check(debugRightAnswer);
    const gameOver = answers.wrong === 0;
    this.progressTracker!.multipleAnswers(
      this.skills.map((skill) => ({
        skill,
        right: answers.right,
        wrong: answers.wrong,
        questions: answers.right + answers.wrong,
      })),
      answers.wrong + answers.right,
      gameOver,
    );

    if (gameOver) {
      this.checkMark!.check(2);
      this.gameOver();
    } else {
      this.checkMark!.x();
      if (this.attempts > 1) this.listSorter!.hint();
    }
  }

  debugRightAnswer() {
    this.checkAnswer(true);
  }

  debugWrongAnswer() {
    this.checkAnswer(false);
  }

  static registry: InteractiveRegistryEntry = {
    description: 'Circle of Fifths Sort',
    skills: ['circle_of_fifths', 'relative_keys'],
  };
}
